
import { Link } from 'react-router-dom';
import { usePublishedPage } from '../hooks/usePublishedPage';
import { useContent } from '../context/ContentContext';
import type { BlogContent } from '../types/cmsPages';
import { PhoneIcon, CalendarIcon } from '@heroicons/react/24/solid';

export default function BlogPostPage() {
  const { settings } = useContent();
  const { page, loading, notFound } = usePublishedPage('blog');
  if (loading) {
    return (
      <div id="main-content" tabIndex={-1} className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-brand-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div id="main-content" tabIndex={-1} className="min-h-screen flex flex-col items-center justify-center px-4">
        <h1 className="text-3xl font-serif font-bold text-brand-navy mb-4">Page Not Found</h1>
        <p className="text-gray-500 mb-6">This blog post doesn't exist or hasn't been published yet.</p>
        <Link to="/" className="text-brand-orange font-semibold hover:text-brand-navy transition-colors">
          Back to Home
        </Link>
      </div>
    );
  }

  const content = page.content as BlogContent;

  return (
    <main id="main-content" tabIndex={-1}>
      <section className="relative bg-brand-navy overflow-hidden">
        <div className="absolute inset-0">
          <svg className="absolute inset-0 w-full h-full opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="blog-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="20" cy="20" r="1" fill="white" />
                <path d="M0 0L40 40M40 0L0 40" stroke="white" strokeWidth="0.5" opacity="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#blog-grid)" />
          </svg>
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-brand-orange/8 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-site mx-auto px-4 sm:px-6 lg:px-8" style={{ paddingTop: '89px', paddingBottom: '89px' }}>
          <nav className="flex items-center gap-2 mb-5 sm:mb-6">
            <Link to="/" className="text-white/50 text-sm hover:text-white/80 transition-colors">Home</Link>
            <svg className="w-3.5 h-3.5 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            <Link to="/blog" className="text-white/50 text-sm hover:text-white/80 transition-colors">Blog</Link>
            <svg className="w-3.5 h-3.5 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-brand-orange text-sm font-medium">{page.title}</span>
          </nav>

          <div className="max-w-3xl">
            <h1 className="text-[32px] leading-[1.15] sm:text-[36px] md:text-4xl lg:text-[40px] xl:text-[44px] font-serif font-bold text-white mb-4">
              {page.title}
            </h1>
            <div className="flex items-center gap-4 text-white/50 text-sm">
              {content.author && <span>By {content.author}</span>}
              {content.publish_date && (
                <span>{new Date(content.publish_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              )}
            </div>
          </div>
        </div>
      </section>

      <article className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="max-w-3xl mx-auto">
          {content.featured_image && (
            <img decoding="async" loading="lazy"
              src={content.featured_image}
              alt={page.title}
              className="w-full h-64 sm:h-80 lg:h-96 object-cover rounded-xl mb-10"
            />
          )}

          {content.excerpt && (
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-8 font-medium border-l-4 border-brand-orange pl-6">
              {content.excerpt}
            </p>
          )}

          <div className="prose prose-lg max-w-none">
            {content.body.split('\n\n').map((paragraph, idx) => (
              <p key={idx} className="text-gray-700 leading-relaxed mb-6">
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        <div className="max-w-3xl mx-auto mt-16 bg-brand-off-white rounded-2xl p-8 sm:p-10">
          <div className="text-center">
            <h3 className="text-2xl font-serif font-bold text-brand-navy mb-3">Ready to Get Started?</h3>
            <p className="text-gray-600 mb-6">Contact us today for a free estimate on your HVAC needs.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={settings.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-brand-orange text-white px-6 py-3.5 rounded-lg font-semibold hover:bg-brand-navy transition-colors"
              >
                <CalendarIcon className="w-5 h-5" />
                Book Online
              </a>
              <a
                href={`tel:${settings.phone.replace(/[^0-9]/g, '')}`}
                className="inline-flex items-center gap-2 bg-brand-navy text-white px-6 py-3.5 rounded-lg font-semibold hover:bg-brand-orange transition-colors"
              >
                <PhoneIcon className="w-5 h-5" />
                {settings.phone}
              </a>
            </div>
          </div>
        </div>
      </article>
    </main>
  );
}
